/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { SOC2Control, SOC2Category, EvidenceType } from '../types/soc2';

export const SOC2_CONTROLS: SOC2Control[] = [
  // Security Category Controls
  {
    id: 'cc1.1',
    number: 'CC1.1',
    title: 'Control Environment and Governance',
    description: 'Management establishes structures, reporting lines, and authorities to achieve objectives.',
    category: 'security',
    criteria: 'security',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 50000,
    maintenanceCost: 10000,
    riskReduction: 15,
    dependencies: [],
    synergies: ['cc1.2', 'cc2.1'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'policy_documents',
        description: 'Governance policies and procedures',
        frequency: 'annually',
        automated: false,
        collectionCost: 5000,
        reviewRequired: true,
        retentionPeriod: 1825
      },
      {
        type: 'training_records',
        description: 'Board and management training records',
        frequency: 'quarterly',
        automated: true,
        collectionCost: 2000,
        reviewRequired: true,
        retentionPeriod: 1825
      }
    ],
    testingProcedures: [
      {
        id: 'cc1.1-01',
        name: 'Governance Structure Review',
        description: 'Review organizational charts and governance documents',
        type: 'inspection',
        frequency: 'annually',
        methodology: 'Document review and interviews with management',
        expectedOutcome: 'Clear governance structure with defined roles and responsibilities',
        evidenceRequired: ['policy_documents'],
        testSteps: [
          'Review organizational chart',
          'Interview key personnel',
          'Verify policy documentation',
          'Assess reporting lines'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 90,
        phases: [
          { name: 'Planning', duration: 15, completed: false },
          { name: 'Policy Development', duration: 30, completed: false },
          { name: 'Implementation', duration: 30, completed: false },
          { name: 'Training', duration: 15, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 25,
      regulatoryCompliance: 30,
      operationalEfficiency: 15,
      riskMitigation: 20,
      competitiveAdvantage: 10
    },
    threats: [
      {
        type: 'governance_failure',
        probability: 15,
        impact: 80,
        description: 'Lack of proper governance controls'
      }
    ]
  },
  {
    id: 'cc6.1',
    number: 'CC6.1',
    title: 'Logical and Physical Access Controls',
    description: 'The entity implements logical access security software, infrastructure, and architectures to protect information assets.',
    category: 'security',
    criteria: 'security',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 100000,
    maintenanceCost: 25000,
    riskReduction: 25,
    dependencies: ['cc6.2'],
    synergies: ['cc6.3', 'cc6.4'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'access_records',
        description: 'User access logs and reviews',
        frequency: 'monthly',
        automated: true,
        collectionCost: 3000,
        reviewRequired: true,
        retentionPeriod: 1095
      },
      {
        type: 'system_logs',
        description: 'Access control system logs',
        frequency: 'continuous',
        automated: true,
        collectionCost: 2000,
        reviewRequired: false,
        retentionPeriod: 365
      }
    ],
    testingProcedures: [
      {
        id: 'cc6.1-01',
        name: 'Access Rights Review',
        description: 'Review user access rights and permissions',
        type: 'inspection',
        frequency: 'quarterly',
        methodology: 'Sample review of user access rights and permissions',
        expectedOutcome: 'Appropriate access controls based on job functions',
        evidenceRequired: ['access_records'],
        testSteps: [
          'Extract user access reports',
          'Review access rights against job descriptions',
          'Identify inappropriate access',
          'Verify remediation actions'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 120,
        phases: [
          { name: 'Assessment', duration: 20, completed: false },
          { name: 'Tool Selection', duration: 15, completed: false },
          { name: 'Implementation', duration: 60, completed: false },
          { name: 'Testing', duration: 25, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 35,
      regulatoryCompliance: 25,
      operationalEfficiency: 20,
      riskMitigation: 15,
      competitiveAdvantage: 5
    },
    threats: [
      {
        type: 'unauthorized_access',
        probability: 20,
        impact: 90,
        description: 'Unauthorized access to sensitive systems'
      }
    ]
  },

  // Availability Category Controls
  {
    id: 'cc4.1',
    number: 'CC4.1',
    title: 'Availability Monitoring and Incident Response',
    description: 'The entity monitors systems for availability and responds to incidents.',
    category: 'availability',
    criteria: 'availability',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 75000,
    maintenanceCost: 20000,
    riskReduction: 30,
    dependencies: ['cc4.2'],
    synergies: ['cc4.3', 'cc6.7'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'monitoring_alerts',
        description: 'System monitoring alerts and responses',
        frequency: 'continuous',
        automated: true,
        collectionCost: 4000,
        reviewRequired: false,
        retentionPeriod: 365
      },
      {
        type: 'incident_reports',
        description: 'Incident reports and resolution documentation',
        frequency: 'as_needed',
        automated: true,
        collectionCost: 1000,
        reviewRequired: true,
        retentionPeriod: 1825
      }
    ],
    testingProcedures: [
      {
        id: 'cc4.1-01',
        name: 'Incident Response Test',
        description: 'Test incident response procedures',
        type: 'reperformance',
        frequency: 'semiannually',
        methodology: 'Simulate security incidents and test response',
        expectedOutcome: 'Effective incident response and recovery',
        evidenceRequired: ['incident_reports', 'monitoring_alerts'],
        testSteps: [
          'Simulate incident scenario',
          'Monitor alert generation',
          'Evaluate response time',
          'Assess resolution effectiveness'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 90,
        phases: [
          { name: 'Planning', duration: 15, completed: false },
          { name: 'Tool Implementation', duration: 45, completed: false },
          { name: 'Procedure Development', duration: 20, completed: false },
          { name: 'Training', duration: 10, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 40,
      regulatoryCompliance: 20,
      operationalEfficiency: 30,
      riskMitigation: 5,
      competitiveAdvantage: 5
    },
    threats: [
      {
        type: 'system_downtime',
        probability: 25,
        impact: 85,
        description: 'Extended system outages affecting business operations'
      }
    ]
  },

  // Processing Integrity Category Controls
  {
    id: 'cc3.1',
    number: 'CC3.1',
    title: 'Data Processing Controls',
    description: 'The entity implements controls to achieve processing integrity.',
    category: 'processing_integrity',
    criteria: 'processing_integrity',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 60000,
    maintenanceCost: 15000,
    riskReduction: 20,
    dependencies: ['cc3.2'],
    synergies: ['cc3.3', 'cc6.8'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'system_logs',
        description: 'Data processing logs and validation results',
        frequency: 'daily',
        automated: true,
        collectionCost: 2500,
        reviewRequired: false,
        retentionPeriod: 730
      },
      {
        type: 'configuration_snapshots',
        description: 'System configuration and validation rules',
        frequency: 'monthly',
        automated: true,
        collectionCost: 1500,
        reviewRequired: true,
        retentionPeriod: 1095
      }
    ],
    testingProcedures: [
      {
        id: 'cc3.1-01',
        name: 'Data Validation Testing',
        description: 'Test data validation and processing controls',
        type: 'reperformance',
        frequency: 'quarterly',
        methodology: 'Process test transactions and validate results',
        expectedOutcome: 'Accurate and complete data processing',
        evidenceRequired: ['system_logs', 'configuration_snapshots'],
        testSteps: [
          'Process test transactions',
          'Validate data accuracy',
          'Check processing completeness',
          'Verify error handling'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 75,
        phases: [
          { name: 'Analysis', duration: 20, completed: false },
          { name: 'Control Design', duration: 25, completed: false },
          { name: 'Implementation', duration: 20, completed: false },
          { name: 'Testing', duration: 10, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 30,
      regulatoryCompliance: 35,
      operationalEfficiency: 25,
      riskMitigation: 5,
      competitiveAdvantage: 5
    },
    threats: [
      {
        type: 'data_corruption',
        probability: 10,
        impact: 95,
        description: 'Corruption or loss of data integrity'
      }
    ]
  },

  // Confidentiality Category Controls
  {
    id: 'cc5.1',
    number: 'CC5.1',
    title: 'Confidential Data Management',
    description: 'The entity meets objectives related to confidentiality',
    category: 'confidentiality',
    criteria: 'confidentiality',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 80000,
    maintenanceCost: 18000,
    riskReduction: 35,
    dependencies: ['cc5.2'],
    synergies: ['cc5.3', 'cc6.1'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'access_records',
        description: 'Confidential data access logs',
        frequency: 'daily',
        automated: true,
        collectionCost: 3000,
        reviewRequired: true,
        retentionPeriod: 1095
      },
      {
        type: 'encryption_records',
        description: 'Data encryption and protection records',
        frequency: 'monthly',
        automated: true,
        collectionCost: 2000,
        reviewRequired: false,
        retentionPeriod: 1825
      }
    ],
    testingProcedures: [
      {
        id: 'cc5.1-01',
        name: 'Data Classification Testing',
        description: 'Test data classification and protection controls',
        type: 'inspection',
        frequency: 'semiannually',
        methodology: 'Review data classification and protection mechanisms',
        expectedOutcome: 'Appropriate protection of confidential data',
        evidenceRequired: ['access_records', 'encryption_records'],
        testSteps: [
          'Review data classification policies',
          'Test classification accuracy',
          'Verify protection mechanisms',
          'Assess access controls'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 100,
        phases: [
          { name: 'Data Assessment', duration: 25, completed: false },
          { name: 'Protection Implementation', duration: 40, completed: false },
          { name: 'Policy Development', duration: 20, completed: false },
          { name: 'Training', duration: 15, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 45,
      regulatoryCompliance: 30,
      operationalEfficiency: 10,
      riskMitigation: 10,
      competitiveAdvantage: 5
    },
    threats: [
      {
        type: 'data_breach',
        probability: 20,
        impact: 95,
        description: 'Unauthorized disclosure of confidential information'
      }
    ]
  },

  // Privacy Category Controls
  {
    id: 'cc7.1',
    number: 'CC7.1',
    title: 'Privacy Notice and Consent',
    description: 'The entity addresses privacy notice and consent requirements.',
    category: 'privacy',
    criteria: 'privacy',
    maturityLevel: 0,
    effectiveness: 0,
    implementationCost: 40000,
    maintenanceCost: 8000,
    riskReduction: 15,
    dependencies: ['cc7.2'],
    synergies: ['cc7.3', 'cc5.1'],
    conflicts: [],
    evidenceRequirements: [
      {
        type: 'policy_documents',
        description: 'Privacy notices and consent records',
        frequency: 'annually',
        automated: false,
        collectionCost: 2000,
        reviewRequired: true,
        retentionPeriod: 1825
      },
      {
        type: 'system_logs',
        description: 'Consent management system logs',
        frequency: 'monthly',
        automated: true,
        collectionCost: 1500,
        reviewRequired: false,
        retentionPeriod: 1095
      }
    ],
    testingProcedures: [
      {
        id: 'cc7.1-01',
        name: 'Privacy Notice Review',
        description: 'Review privacy notice and consent mechanisms',
        type: 'inspection',
        frequency: 'annually',
        methodology: 'Review privacy documentation and consent processes',
        expectedOutcome: 'Compliant privacy notice and consent management',
        evidenceRequired: ['policy_documents', 'system_logs'],
        testSteps: [
          'Review privacy notice content',
          'Test consent mechanisms',
          'Verify record keeping',
          'Assess regulatory compliance'
        ]
      }
    ],
    implementation: {
      status: 'not_implemented',
      lastUpdated: 0,
      implementedBy: [],
      resources: [],
      timeline: {
        estimatedDuration: 60,
        phases: [
          { name: 'Legal Review', duration: 20, completed: false },
          { name: 'Notice Development', duration: 15, completed: false },
          { name: 'System Implementation', duration: 15, completed: false },
          { name: 'Testing', duration: 10, completed: false }
        ]
      },
      documentation: [],
      exceptions: []
    },
    businessImpact: {
      customerTrust: 35,
      regulatoryCompliance: 40,
      operationalEfficiency: 5,
      riskMitigation: 15,
      competitiveAdvantage: 5
    },
    threats: [
      {
        type: 'privacy_violation',
        probability: 15,
        impact: 80,
        description: 'Non-compliance with privacy regulations'
      }
    ]
  }
];

export const SOC2_CONTROL_DEPENDENCIES = {
  'cc1.1': [],
  'cc6.1': ['cc6.2'],
  'cc4.1': ['cc4.2'],
  'cc3.1': ['cc3.2'],
  'cc5.1': ['cc5.2'],
  'cc7.1': ['cc7.2']
};

export const SOC2_CONTROL_SYNERGIES = {
  'cc1.1': ['cc1.2', 'cc2.1'],
  'cc6.1': ['cc6.3', 'cc6.4'],
  'cc4.1': ['cc4.3', 'cc6.7'],
  'cc3.1': ['cc3.3', 'cc6.8'],
  'cc5.1': ['cc5.3', 'cc6.1'],
  'cc7.1': ['cc7.3', 'cc5.1']
};

export const SOC2_CATEGORY_DESCRIPTIONS = {
  security: 'Security - Controls protect information from unauthorized access',
  availability: 'Availability - Controls ensure system accessibility for operations',
  processing_integrity: 'Processing Integrity - Controls ensure complete, accurate processing',
  confidentiality: 'Confidentiality - Controls protect sensitive information from unauthorized disclosure',
  privacy: 'Privacy - Controls address personal information collection and use'
};

export const SOC2_CRITERIA_DESCRIPTIONS = {
  security: 'Information and systems are protected against unauthorized access, unauthorized disclosure of information, and damage to systems that could compromise the availability, integrity, confidentiality, and privacy of information or systems and affect the entity\'s ability to meet its objectives.',
  availability: 'Information and systems are available for operation and use to meet the entity\'s objectives.',
  processing_integrity: 'System processing is complete, accurate, timely, and authorized to meet the entity\'s objectives.',
  confidentiality: 'Information designated as confidential is protected to meet the entity\'s objectives.',
  privacy: 'Personal information is collected, used, retained, disclosed, and disposed to meet the entity\'s objectives.'
};

export const SOC2_IMPLEMENTATION_PHASES = [
  { id: 'assessment', name: 'Assessment', description: 'Evaluate current state and requirements', typicalDuration: 15 },
  { id: 'planning', name: 'Planning', description: 'Develop implementation plan and resource allocation', typicalDuration: 10 },
  { id: 'design', name: 'Design', description: 'Create detailed control designs', typicalDuration: 20 },
  { id: 'implementation', name: 'Implementation', description: 'Implement controls and procedures', typicalDuration: 30 },
  { id: 'testing', name: 'Testing', description: 'Test control effectiveness', typicalDuration: 15 },
  { id: 'documentation', name: 'Documentation', description: 'Create and maintain documentation', typicalDuration: 10 }
];

export const SOC2_EVIDENCE_TYPES = {
  system_logs: 'System Logs and Monitoring Records',
  access_records: 'Access Control Records and Reviews',
  configuration_snapshots: 'Configuration and Change Management Records',
  vulnerability_scans: 'Vulnerability Assessment and Penetration Test Results',
  penetration_tests: 'Security Testing and Assessment Reports',
  risk_assessments: 'Risk Assessment and Analysis Reports',
  policy_documents: 'Policies, Procedures, and Standards',
  training_records: 'Training and Awareness Records',
  incident_reports: 'Security Incident Reports and Response Records',
  monitoring_alerts: 'Security Monitoring Alerts and Responses'
};